// src/stock/MouvementsStock.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Table, Row, Col, Form, Button,
    Tooltip, OverlayTrigger, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatDate, formatDateTime } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function MouvementsStock() {
    const navigate = useNavigate();
    const [mouvements, setMouvements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filterType, setFilterType] = useState('');
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');
    const [stats, setStats] = useState(null);
    const [viewMode, setViewMode] = useState('table');
    const [sortConfig, setSortConfig] = useState({ key: 'date_mouvement', direction: 'desc' });
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchMouvements();
        fetchStats();
    }, [filterType, dateDebut, dateFin]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchMouvements = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/stock_etat.php?action=mouvements`;

            const params = new URLSearchParams();
            if (filterType) params.append('type_mouvement', filterType);
            if (dateDebut) params.append('date_debut', dateDebut);
            if (dateFin) params.append('date_fin', dateFin);

            if (params.toString()) url += '&' + params.toString();

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error("Erreur chargement");

            const result = await response.json();
            const dataWithNumbers = (result.data || []).map(m => ({
                ...m,
                quantite: Number(m.quantite) || 0,
                stock_avant: Number(m.stock_avant) || 0,
                stock_apres: Number(m.stock_apres) || 0
            }));

            setMouvements(dataWithNumbers);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/stock_etat.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getTypeBadge = (type) => {
        const configs = {
            entree:      { gradient: 'linear-gradient(135deg, #10b981, #059669)', label: 'Entrée',      icon: 'ri-add-line' },
            sortie:      { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', label: 'Sortie',      icon: 'ri-subtract-line' },
            inventaire:  { gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)', label: 'Inventaire',  icon: 'ri-file-list-line' },
            reservation: { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', label: 'Réservation', icon: 'ri-bookmark-line' },
            annulation:  { gradient: 'linear-gradient(135deg, #64748b, #475569)', label: 'Annulation',  icon: 'ri-close-circle-line' }
        };
        const config = configs[type] || configs.sortie;
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children, className }) => (
        <th className={className} onClick={() => handleSort(field)} style={{ cursor: 'pointer', userSelect: 'none' }}>
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const resetFilters = () => {
        setFilterText('');
        setFilterType('');
        setDateDebut('');
        setDateFin('');
    };

    /* ============================================================
       FILTER / SORT / PAGINATION
       ============================================================ */
    const filteredMouvements = mouvements
        .filter(m =>
            m.reference?.toLowerCase().includes(filterText.toLowerCase()) ||
            m.designation?.toLowerCase().includes(filterText.toLowerCase())
        )
        .sort((a, b) => {
            if (sortConfig.key === 'date_mouvement') {
                const dateA = new Date(a.date_mouvement);
                const dateB = new Date(b.date_mouvement);
                return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
            }
            if (sortConfig.key === 'quantite') {
                return sortConfig.direction === 'asc'
                    ? (a.quantite || 0) - (b.quantite || 0)
                    : (b.quantite || 0) - (a.quantite || 0);
            }
            return 0;
        });

    const totalPages = Math.ceil(filteredMouvements.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredMouvements.slice(startIndex, startIndex + itemsPerPage);

    const totalEntrees = mouvements
        .filter(m => m.type_mouvement === 'entree')
        .reduce((sum, m) => sum + (Number(m.quantite) || 0), 0);

    const totalSorties = mouvements
        .filter(m => m.type_mouvement === 'sortie')
        .reduce((sum, m) => sum + (Number(m.quantite) || 0), 0);

    /* ============================================================
       LOADING — identique à l'original
       ============================================================ */
    if (loading && !mouvements.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center" style={{ maxWidth: '400px' }}>
                            <div className="loading-ring-wrapper mb-4">
                                <div className="loading-ring"></div>
                                <div className="loading-ring-inner"></div>
                                <i className="ri-loader-4-fill loading-icon"></i>
                            </div>
                            <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                Chargement des mouvements...
                            </h5>
                            <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                            <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                    <div
                                        className="progress-bar progress-bar-striped progress-bar-animated"
                                        style={{
                                            width: '100%',
                                            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                            backgroundSize: '200% 100%',
                                            animation: 'shimmer 1.5s linear infinite'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                    .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                `}</style>
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-history-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Mouvements de stock</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-bar-chart-line"></i> Total {filteredMouvements.length}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-add-line"></i> {totalEntrees} entrées
                                    </span>
                                    {totalSorties > 0 && (
                                        <span className="badge-modern" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                            <i className="ri-subtract-line"></i> {totalSorties} sorties
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchMouvements} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-outline" onClick={() => navigate('/stock/etat/instantane')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="4">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-stack-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Total mouvements</span>
                                    <h3 className="kpi-value-charte">{filteredMouvements.length}</h3>
                                    <span className="kpi-unit-charte">tous types confondus</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="4">
                            <div className="kpi-charte kpi-success">
                                <div className="kpi-icon-charte"><i className="ri-arrow-down-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Total entrées</span>
                                    <h3 className="kpi-value-charte">{totalEntrees}</h3>
                                    <span className="kpi-unit-charte">articles reçus</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="4">
                            <div className="kpi-charte kpi-danger">
                                <div className="kpi-icon-charte"><i className="ri-arrow-up-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Total sorties</span>
                                    <h3 className="kpi-value-charte">{totalSorties}</h3>
                                    <span className="kpi-unit-charte">articles sortis</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="3">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="2">
                                    <Form.Select
                                        value={filterType}
                                        onChange={(e) => setFilterType(e.target.value)}
                                        className="filter-select-charte"
                                    >
                                        <option value="">Tous types</option>
                                        <option value="entree">Entrées</option>
                                        <option value="sortie">Sorties</option>
                                        <option value="inventaire">Inventaires</option>
                                        <option value="reservation">Réservations</option>
                                        <option value="annulation">Annulations</option>
                                    </Form.Select>
                                </Col>
                                <Col md="2">
                                    <Form.Control
                                        type="date"
                                        value={dateDebut}
                                        onChange={(e) => setDateDebut(e.target.value)}
                                        className="filter-select-charte"
                                        title="Date début"
                                    />
                                </Col>
                                <Col md="2">
                                    <Form.Control
                                        type="date"
                                        value={dateFin}
                                        onChange={(e) => setDateFin(e.target.value)}
                                        className="filter-select-charte"
                                        title="Date fin"
                                    />
                                </Col>
                                <Col md="2">
                                    <Form.Select
                                        value={itemsPerPage}
                                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                        className="filter-select-charte"
                                    >
                                        <option value={5}>5 / page</option>
                                        <option value={10}>10 / page</option>
                                        <option value={25}>25 / page</option>
                                        <option value={50}>50 / page</option>
                                    </Form.Select>
                                </Col>
                                <Col md="1" className="d-flex justify-content-end gap-2">
                                    <button
                                        className="btn-filter-reset-charte"
                                        onClick={resetFilters}
                                        title="Réinitialiser"
                                    >
                                        <i className="ri-refresh-line"></i>
                                    </button>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md="6">
                                    <Form.Select
                                        value={sortConfig.key}
                                        onChange={(e) => setSortConfig({ key: e.target.value, direction: 'desc' })}
                                        className="filter-select-charte"
                                    >
                                        <option value="date_mouvement">Trier par date</option>
                                        <option value="quantite">Trier par quantité</option>
                                    </Form.Select>
                                </Col>
                                <Col md="6" className="d-flex justify-content-end gap-2">
                                    <button
                                        className={`view-toggle-charte ${viewMode === 'table' ? 'active' : ''}`}
                                        onClick={() => setViewMode('table')}
                                        title="Vue tableau"
                                    >
                                        <i className="ri-table-line"></i>
                                    </button>
                                    <button
                                        className={`view-toggle-charte ${viewMode === 'cards' ? 'active' : ''}`}
                                        onClick={() => setViewMode('cards')}
                                        title="Vue cartes"
                                    >
                                        <i className="ri-grid-line"></i>
                                    </button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== VUE TABLEAU ===== */}
                    {viewMode === 'table' && (
                        <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                            <Card.Header className="card-header-gradient">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-history-line text-white fs-5"></i>
                                        <span className="fw-semibold text-white">Historique des mouvements</span>
                                        <span className="badge-header-light">{filteredMouvements.length}</span>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0 table-charte">
                                        <thead>
                                            <tr>
                                                <SortableHeader field="date_mouvement">Date</SortableHeader>
                                                <th className="text-center">Type</th>
                                                <th>Produit</th>
                                                <th>Réf.</th>
                                                <SortableHeader field="quantite" className="text-center">Quantité</SortableHeader>
                                                <th className="text-center">Stock avant</th>
                                                <th className="text-center">Stock après</th>
                                                <th>Document</th>
                                                <th>Utilisateur</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentItems.length > 0 ? (
                                                currentItems.map((m) => (
                                                    <tr key={m.id_mouvement}>
                                                        <td>
                                                            <div className="d-flex flex-column">
                                                                <span className="fw-semibold">{formatDate(m.date_mouvement)}</span>
                                                                <small className="text-muted">
                                                                    <i className="ri-time-line me-1"></i>
                                                                    {formatDateTime(m.date_mouvement).split(' ')[1]}
                                                                </small>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">{getTypeBadge(m.type_mouvement)}</td>
                                                        <td>
                                                            <Link to={`/stock/produits/${m.id_produit}`} className="table-link-charte">
                                                                {m.designation}
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            <span className="reference-badge-charte">{m.reference}</span>
                                                        </td>
                                                        <td className="text-center">
                                                            <span
                                                                className="fw-bold"
                                                                style={{
                                                                    fontSize: '1.1rem',
                                                                    color: m.type_mouvement === 'entree'
                                                                        ? COLORS.success
                                                                        : m.type_mouvement === 'sortie'
                                                                            ? COLORS.danger
                                                                            : COLORS.info
                                                                }}
                                                            >
                                                                {m.type_mouvement === 'entree' ? '+' :
                                                                 m.type_mouvement === 'sortie' ? '−' : ''}{m.quantite}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="quantity-badge-charte">{m.stock_avant}</span>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="quantity-badge-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                                {m.stock_apres}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {m.reference_document ? (
                                                                <div className="d-flex flex-column">
                                                                    <span className="status-badge-charte mb-1" style={{ background: 'linear-gradient(135deg, #06b6d4, #0891b2)' }}>
                                                                        <i className="ri-file-text-line"></i>
                                                                        {m.type_document}
                                                                    </span>
                                                                    <small className="text-muted">{m.reference_document}</small>
                                                                </div>
                                                            ) : <span className="text-muted">—</span>}
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-1">
                                                                <i className="ri-user-line text-muted"></i>
                                                                <span className="small">{m.prenom_utilisateur} {m.nom_utilisateur}</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="9">
                                                        <div className="empty-state-charte">
                                                            <div className="empty-state-icon-charte">
                                                                <i className="ri-history-line"></i>
                                                            </div>
                                                            <h6 className="fw-semibold mb-1">Aucun mouvement trouvé</h6>
                                                            <p className="text-muted small mb-0">
                                                                {filterText || filterType || dateDebut || dateFin
                                                                    ? "Aucun résultat ne correspond à vos filtres"
                                                                    : "Aucun mouvement de stock enregistré"}
                                                            </p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* ===== VUE CARTES ===== */}
                    {viewMode === 'cards' && (
                        <Row className="g-4">
                            {currentItems.length > 0 ? (
                                currentItems.map((m) => (
                                    <Col lg={6} xl={4} key={m.id_mouvement}>
                                        <Card className="border-0 shadow-sm rounded-3 h-100 mouvement-card-charte">
                                            <Card.Body className="p-4">
                                                <div className="d-flex justify-content-between align-items-start mb-3">
                                                    <div>
                                                        {getTypeBadge(m.type_mouvement)}
                                                        <h5 className="mb-1 mt-2">{m.designation}</h5>
                                                        <small className="text-muted">
                                                            <i className="ri-barcode-line me-1"></i>
                                                            {m.reference}
                                                        </small>
                                                    </div>
                                                    <div className="text-end">
                                                        <span
                                                            className="fw-bold"
                                                            style={{
                                                                fontSize: '1.75rem',
                                                                color: m.type_mouvement === 'entree'
                                                                    ? COLORS.success
                                                                    : m.type_mouvement === 'sortie'
                                                                        ? COLORS.danger
                                                                        : COLORS.info
                                                            }}
                                                        >
                                                            {m.type_mouvement === 'entree' ? '+' :
                                                             m.type_mouvement === 'sortie' ? '−' : ''}{m.quantite}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="mb-3">
                                                    <div className="d-flex justify-content-between mb-2 flex-wrap gap-2">
                                                        <small className="text-muted">
                                                            <i className="ri-time-line me-1"></i>
                                                            {formatDateTime(m.date_mouvement)}
                                                        </small>
                                                        <small className="text-muted">
                                                            <i className="ri-user-line me-1"></i>
                                                            {m.prenom_utilisateur} {m.nom_utilisateur}
                                                        </small>
                                                    </div>

                                                    <div className="stock-info-box-charte">
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span className="text-muted small">Stock avant</span>
                                                            <span className="fw-semibold">{m.stock_avant}</span>
                                                        </div>
                                                        <div className="d-flex justify-content-between">
                                                            <span className="text-muted small">Stock après</span>
                                                            <span className="fw-bold" style={{ color: COLORS.primary }}>{m.stock_apres}</span>
                                                        </div>
                                                    </div>

                                                    {m.reference_document && (
                                                        <div className="mt-2 small text-muted">
                                                            <i className="ri-file-text-line me-1"></i>
                                                            {m.type_document}: {m.reference_document}
                                                        </div>
                                                    )}
                                                </div>

                                                <hr className="my-3" />

                                                <div className="d-flex justify-content-end">
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Voir le produit</Tooltip>}>
                                                        <button
                                                            className="action-btn-charte"
                                                            onClick={() => navigate(`/stock/produits/${m.id_produit}`)}
                                                            style={{ '--btn-color': COLORS.primary }}
                                                        >
                                                            <i className="ri-eye-line"></i>
                                                        </button>
                                                    </OverlayTrigger>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))
                            ) : (
                                <Col md={12}>
                                    <div className="empty-state-charte">
                                        <div className="empty-state-icon-charte">
                                            <i className="ri-history-line"></i>
                                        </div>
                                        <h6 className="fw-semibold mb-1">Aucun mouvement trouvé</h6>
                                        <p className="text-muted small mb-0">Aucun résultat ne correspond à vos filtres</p>
                                    </div>
                                </Col>
                            )}
                        </Row>
                    )}

                    {/* ===== PAGINATION ===== */}
                    {totalPages > 1 && !loading && (
                        <div className="pagination-wrapper-charte mt-3">
                            <div className="pagination-info-charte">
                                Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredMouvements.length)}</strong> sur <strong>{filteredMouvements.length}</strong> mouvements
                            </div>
                            <Pagination className="pagination-charte mb-0">
                                <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) pageNum = i + 1;
                                    else if (currentPage <= 3) pageNum = i + 1;
                                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                    else pageNum = currentPage - 2 + i;
                                    return (
                                        <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => setCurrentPage(pageNum)}>
                                            {pageNum}
                                        </Pagination.Item>
                                    );
                                })}
                                <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
                            </Pagination>
                        </div>
                    )}
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .search-box-charte { position: relative; }
                .search-icon-charte {
                    position: absolute; top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--primary); font-size: 1rem;
                    z-index: 2; pointer-events: none;
                }
                .search-input-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem 0.6rem 2.75rem !important;
                    font-size: 0.9rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .filter-select-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.85rem;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .btn-filter-reset-charte {
                    background: rgba(255,255,255,0.15);
                    border: 1.5px solid rgba(255,255,255,0.3);
                    color: white;
                    width: 40px; height: 38px;
                    border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1.1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .btn-filter-reset-charte:hover {
                    background: rgba(255,255,255,0.25);
                    border-color: rgba(255,255,255,0.5);
                }
                .view-toggle-charte {
                    width: 40px; height: 38px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: rgba(255,255,255,0.15);
                    border: 1.5px solid rgba(255,255,255,0.3);
                    color: white; font-size: 1.1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .view-toggle-charte:hover { background: rgba(255,255,255,0.25); }
                .view-toggle-charte.active {
                    background: white;
                    color: var(--primary);
                    border-color: white;
                }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-link-charte {
                    color: var(--primary);
                    text-decoration: none;
                    font-weight: 600;
                    font-size: 0.85rem;
                }
                .table-link-charte:hover { text-decoration: underline; }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.3rem 0.75rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                    white-space: nowrap;
                }
                .quantity-badge-charte {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 45px;
                    padding: 0.25rem 0.6rem;
                    background: #f1f5f9;
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 0.8rem;
                    color: #475569;
                }

                /* ===== CARDS ===== */
                .mouvement-card-charte {
                    transition: all 0.3s ease;
                }
                .mouvement-card-charte:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 30px rgba(0,0,0,0.08) !important;
                }
                .stock-info-box-charte {
                    background: #f8fafc;
                    border-radius: 12px;
                    padding: 0.85rem 1rem;
                    border: 1px solid #e2e8f0;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem 1.5rem;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    flex-wrap: wrap; gap: 1rem;
                }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link {
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 0.9rem;
                    font-weight: 500; font-size: 0.85rem;
                    transition: all 0.2s;
                }
                .pagination-charte .page-item.active .page-link {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .pagination-charte .page-item .page-link:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    color: var(--primary);
                    transform: translateY(-1px);
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-outline:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}